import { ModelElementType } from "@/components/entities";
import { create } from "zustand";
import {
  fetchModels,
  setModelStarred,
} from "@/components/main/utils/models/ModelServices";
import { useTeamsStore } from "./teamsStore";
import { fetchWithInit, FetchListOptions } from "./fetchWithInit";
import {
  PendingLifecycleMap,
  PendingPatchMap,
  reconcile,
  rollbackLifecycle,
  rollbackPatch,
  stagePatch,
} from "./pendingModelMutations";
import { TrashModel } from "target_wasm";

interface MainState {
  selectedModel: ModelElementType | null;
  setSelectedModel: (model: ModelElementType | null) => void;
  selectedTrashModel: TrashModel | null;
  setSelectedTrashModel: (model: TrashModel | null) => void;
  availableModels: ModelElementType[];
  /** Local mutations no models response has echoed yet — see
   *  `pendingModelMutations`. Retired on echo, or after the grace window. */
  pendingLifecycle: PendingLifecycleMap;
  pendingPatches: PendingPatchMap;
  setAvailableModels: (models: ModelElementType[]) => void;
  removeModelFromAvailable: (model_id: string) => void;
  /**
   * Optimistically apply `fields` to a model and stage them against stale
   * polls. Returns a rollback that restores exactly these fields — call it
   * when the server request fails.
   */
  patchModel: (
    modelId: string,
    fields: Partial<ModelElementType>,
  ) => () => void;
  teamModelAccess: Record<string, Record<string, string>>;
  setTeamModelAccess: (
    teamId: string,
    modelId: string,
    accessLabel: string,
  ) => void;
  getTeamModelAccess: (teamId: string, modelId: string) => string | undefined;
  isLoading: boolean;
  error: string | null;
  fetchModelsList: (options?: FetchListOptions) => Promise<ModelElementType[]>;
  /** Refetch guaranteed to postdate the call — for after a mutation. */
  invalidateModels: () => Promise<ModelElementType[]>;
  addModel: (model: ModelElementType) => void;
  updateModel: (model: ModelElementType) => void;
  /**
   * Optimistically flip `is_starred` for a model and persist via the API.
   * Rolls back the local state if the request fails. Resolves to the final
   * `is_starred` value (which equals the requested target on success, or the
   * original value on failure).
   */
  toggleStar: (modelId: string, nextStarred: boolean) => Promise<boolean>;
  setError: (message: string | null) => void;
}

export const useMainStateStore = create<MainState>((set, get) => ({
  selectedModel: null,
  setSelectedModel: (model) => {
    set({ selectedModel: model });
  },
  selectedTrashModel: null,
  setSelectedTrashModel: (model) => {
    set({ selectedTrashModel: model });
  },
  availableModels: [],
  pendingLifecycle: {},
  pendingPatches: {},
  setAvailableModels: (models) => {
    const {
      models: merged,
      lifecycle,
      patches,
    } = reconcile(models, get().pendingLifecycle, get().pendingPatches);

    const selModelId = get().selectedModel?.model_id;
    const refreshed = selModelId
      ? merged.find((m) => m.model_id === selModelId)
      : undefined;
    set({
      availableModels: merged,
      pendingLifecycle: lifecycle,
      pendingPatches: patches,
      selectedModel: refreshed
        ? refreshed
        : selModelId && !merged.some((e) => e.model_id === selModelId)
          ? null
          : get().selectedModel,
    });
  },
  removeModelFromAvailable: (model_id: string) => {
    set((state) => {
      const row = state.availableModels.find((m) => m.model_id === model_id);
      return {
        availableModels: state.availableModels.filter(
          (m) => m.model_id !== model_id,
        ),
        // An unconfirmed add just clears; anything else leaves a tombstone so
        // a stale poll can't resurrect the row.
        pendingLifecycle:
          state.pendingLifecycle[model_id]?.kind === "add" || !row
            ? rollbackLifecycle(state.pendingLifecycle, model_id)
            : {
                ...state.pendingLifecycle,
                [model_id]: { kind: "remove", at: Date.now(), row },
              },
      };
    });
    const { teamModels, removeModelFromTeam } = useTeamsStore.getState();
    Object.entries(teamModels).forEach(([teamId, models]) => {
      if (models?.some((m) => m.model_id === model_id)) {
        removeModelFromTeam(teamId, model_id);
      }
    });
  },
  patchModel: (modelId, fields) => {
    const current = get().availableModels.find((m) => m.model_id === modelId);
    if (!current) return () => {};
    set((state) => ({
      pendingPatches: stagePatch(
        state.pendingPatches,
        modelId,
        fields,
        current,
      ),
    }));
    get().updateModel({ ...current, ...fields });

    const keys = Object.keys(fields) as (keyof ModelElementType)[];
    return () => {
      const { patches, restore } = rollbackPatch(
        get().pendingPatches,
        modelId,
        keys,
      );
      set({ pendingPatches: patches });
      const row = get().availableModels.find((m) => m.model_id === modelId);
      if (row) get().updateModel({ ...row, ...restore });
    };
  },
  teamModelAccess: {},
  setTeamModelAccess: (teamId, modelId, accessLabel) => {
    if (!teamId || !modelId || !accessLabel) return;
    const existingModel = get().availableModels.find(
      (m) => m.model_id === modelId,
    );
    if (existingModel)
      get().updateModel({ ...existingModel, team_access: accessLabel });
    set((state) => ({
      teamModelAccess: {
        ...state.teamModelAccess,
        [teamId]: {
          ...(state.teamModelAccess[teamId] || {}),
          [modelId]: accessLabel,
        },
      },
    }));
  },
  getTeamModelAccess: (teamId, modelId) =>
    teamId && modelId ? get().teamModelAccess[teamId]?.[modelId] : undefined,
  isLoading: false,
  error: null,
  fetchModelsList: async (options) => {
    return fetchWithInit(set, options, {
      initFlag: "modelsInitialized",
      dataKey: "availableModels",
      cachedData: get().availableModels,
      fetchFn: fetchModels,
      setData: (models) => get().setAvailableModels(models),
      errorMessage: "Failed to fetch models",
    });
  },
  invalidateModels: () => get().fetchModelsList({ refresh: true, force: true }),
  updateModel: (updatedModel) => {
    set((state) => {
      const existingIndex = state.availableModels.findIndex(
        (m) => m.model_id === updatedModel.model_id,
      );
      const pending = state.pendingLifecycle[updatedModel.model_id];
      return {
        availableModels:
          existingIndex >= 0
            ? state.availableModels.map((m) =>
                m.model_id === updatedModel.model_id ? updatedModel : m,
              )
            : [...state.availableModels, updatedModel],
        // A pending add re-inserts its staged row on reconcile — keep that
        // snapshot current so edits made meanwhile aren't re-inserted stale.
        pendingLifecycle:
          pending?.kind === "add"
            ? {
                ...state.pendingLifecycle,
                [updatedModel.model_id]: { ...pending, row: updatedModel },
              }
            : state.pendingLifecycle,
        selectedModel:
          state.selectedModel?.model_id === updatedModel.model_id
            ? updatedModel
            : state.selectedModel,
      };
    });
    useTeamsStore.getState().updateModel(updatedModel);
  },
  addModel: (model) => {
    const isNew = !get().availableModels.some(
      (m) => m.model_id === model.model_id,
    );
    if (isNew)
      set((state) => ({
        pendingLifecycle: {
          ...state.pendingLifecycle,
          [model.model_id]: { kind: "add", at: Date.now(), row: model },
        },
      }));
    get().updateModel(model);
  },
  toggleStar: async (modelId, nextStarred) => {
    const current = get().availableModels.find((m) => m.model_id === modelId);
    if (!current) return nextStarred;
    const previous = current.is_starred ?? false;
    if (previous === nextStarred) return nextStarred;

    const rollback = get().patchModel(modelId, { is_starred: nextStarred });

    const ok = await setModelStarred(modelId, nextStarred);
    if (!ok) {
      rollback();
      return previous;
    }
    return nextStarred;
  },
  setError: (message) => set({ error: message }),
}));
